import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import SearchBar from "../SearchBar/SearchBar";
import SearchBarAnimator from "../SearchBarAnimator/SearchBarAnimator";
import styles from "./SearchBarInjector.module.css";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Constants from "../../utils/Constants";
import FocusLock from "react-focus-lock";

const SearchBarInjector = ({
   merchantId,
   isSearchBarVisible,
   onStartSearch,
   onSearchSubmitted,
   onQueryUpdated,
   onCloseSearchBar,
   brandTheme,
   assistantConfiguration,
   isDrawerOpen,
   log
}) => {
    const injectedContainerRef = useRef(null);
    const timeoutRef = useRef(null);

    // Create the container once (when config changes)
    useEffect(() => {
        if (!assistantConfiguration) return;
        if (assistantConfiguration.searchBar) {
            return createSearchBarContainer(assistantConfiguration, injectedContainerRef, timeoutRef);
        }
    }, [assistantConfiguration]);

    useEffect(() => {
        // Define the handler that checks if the click was outside the search bar container.
        const handleClickOutside = (event) => {
            // Only apply on mobile devices.
            if (window.innerWidth > Constants.MaxWidthForMobile) {
                return;
            }

            // If the injected container exists and the click target is not inside it, close the search bar.
            if (injectedContainerRef.current && !injectedContainerRef.current.contains(event.target)) {
                onCloseSearchBar();
            }
        };

        // Attach the event listener when the search bar is visible.
        if (isSearchBarVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Clean up the event listener on unmount or when isSearchBarVisible changes.
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSearchBarVisible, onCloseSearchBar]);

    // Handle mounting and unmounting based on visibility
    useEffect(() => {
        if (!injectedContainerRef.current || !assistantConfiguration) {
            console.info("Nobi: No injected container or assistant configuration found");
            return;
        }
        ReactDOM.render(
            <FocusLock disabled={!isSearchBarVisible || isDrawerOpen} returnFocus={true} data-testid="search-bar-injector">
                <SearchBarAnimator
                    isVisible={isSearchBarVisible}
                    animation={assistantConfiguration.searchBar?.searchBarAnimation}
                    brandTheme={brandTheme}
                >
                    <SearchBar
                        merchantId={merchantId}
                        onSearchSubmitted={onSearchSubmitted}
                        onQueryUpdated={onQueryUpdated}
                        brandTheme={brandTheme}
                        isDrawerOpen={isDrawerOpen}
                        isSearchBarVisible={isSearchBarVisible}
                    />
                    <IconButton
                        aria-label="Close"
                        id={styles.closeIcon}
                        style={{ color: brandTheme?.searchBar?.injectedContainer?.mobile?.closeButtonColor || "#FFF" }}
                        onClick={onCloseSearchBar}
                    >
                        <CloseIcon />
                    </IconButton>
                </SearchBarAnimator>
            </FocusLock>,
            injectedContainerRef.current
        );

        if (!isSearchBarVisible) {
            timeoutRef.current = setTimeout(() => {
                ReactDOM.unmountComponentAtNode(injectedContainerRef.current);
            }, 300);
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isSearchBarVisible, assistantConfiguration, brandTheme, onSearchSubmitted, onQueryUpdated, onCloseSearchBar]);

    // Attach event listener to the search icon based on configuration
    useEffect(() => {
        if (!assistantConfiguration?.searchBar?.searchIconSelector) {
            console.info("No search icon selector provided in assistant configuration");
            return;
        }

        const searchIcons = document.querySelectorAll(assistantConfiguration.searchBar.searchIconSelector);
        if (searchIcons.length === 0) {
            console.error(`No search icons found for selector: ${assistantConfiguration.searchBar.searchIconSelector}`);
            return;
        }

        const handleIconClick = (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            // Always show the injected search bar
            onStartSearch();
            log && log("Search icon activated");
        };

        searchIcons.forEach(icon => {
            icon.addEventListener("click", handleIconClick, true);
        });

        return () => {
            searchIcons.forEach(icon => {
                icon.removeEventListener("click", handleIconClick, true);
            });
        };
    }, [assistantConfiguration, onStartSearch, log]);

    function getContainerNeighbor(assistantConfiguration) {
        if (assistantConfiguration?.searchBar?.searchBarSiblingSelector && assistantConfiguration?.searchBar?.searchBarSiblingSelector) {
            return document.querySelector(assistantConfiguration?.searchBar?.searchBarSiblingSelector);
        }
        else if (assistantConfiguration?.searchBar?.searchIconSelector) {
            return document.querySelector(assistantConfiguration?.searchBar?.searchIconSelector);
        }
        else {
            log && log("No search bar neighbor selector found in assistant configuration");
            return null;
        }
    }

    function insertContainerNearNeighbor(assistantConfiguration, containerNeighbor, container) {
        if (window.innerWidth <= Constants.MaxWidthForMobile) {
            document.body.appendChild(container);
            return;
        }

        if (!containerNeighbor) {
            console.error(`Search bar neighbor not found with selector: ${assistantConfiguration?.searchBar?.searchBarSiblingSelector}`);
            return;
        }

        if (assistantConfiguration?.searchBar?.searchBarAnimation === "left-to-right") {
            containerNeighbor.insertAdjacentElement("afterend", container);
        }
        else {
            containerNeighbor.insertAdjacentElement("beforebegin", container);
        }
    }

    function cleanupCallback(container, timeoutRef, injectedContainerRef) {
        return () => {
            container.remove();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            injectedContainerRef.current = null;
        };
    }

    function createSearchBarContainer(assistantConfiguration, injectedContainerRef, timeoutRef) {
        const container = document.createElement("div");
        container.id = styles.nobiSearchBarInjectedContainer;
        let containerNeighbor = getContainerNeighbor(assistantConfiguration);
        if (containerNeighbor || window.innerWidth <= Constants.MaxWidthForMobile) {
            insertContainerNearNeighbor(assistantConfiguration, containerNeighbor, container);
            injectedContainerRef.current = container;
            return cleanupCallback(container, timeoutRef, injectedContainerRef);
        }
        else {
            console.info("No container neighbor found for search bar injection or mobile site detected");
            return () => {};
        }
    }

    return null;
};

export default SearchBarInjector;
